import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { stringToDate } from '@dxb-web-ui-framework/common/utils';
import { subMinutes, subYears } from 'date-fns';
import { transactionService } from '../../../../channels/secure-web/services';
import { DownloadTypes } from '../../../content-composer/src/components/Generic/FileDownload/FileDownload';
import {
  useAccountDetailsSelectedAccount,
  useTransactionListFilters,
} from '../../../account/src/contexts/AccountDetails/hooks';
import { downloadFile, mappingTransactionType } from '../../../content-composer/src/utils';

export type AccountExtraitTransactionsToDownload = {
  type: DownloadTypes;
  meta: {
    timestamp: string;
  };
};

const useDownloadAccountExtraitTransactions = () => {
  const [accountExtraitTransactionsToDownload, setAccountExtraitTransactionsToDownload] =
    useState<Nullable<AccountExtraitTransactionsToDownload>>(null);

  const { t } = useTranslation();
  const accountNumber = useAccountDetailsSelectedAccount()?.accountNumber;
  const accountExtraitTransactionListFilters = useTransactionListFilters();

  const { data: accountExtraitTransactionsDownloadData, isLoading: isLoadingAccountExtraitTransactionsDownloadData } =
    useInvokeSWRService(
      transactionService.GetAccountTransactionsPrint,
      {
        accountNumber,
        ...accountExtraitTransactionListFilters,
        dateFrom: accountExtraitTransactionListFilters.dateFrom
          ? accountExtraitTransactionListFilters.dateFrom
          : stringToDate({
              date: subYears(new Date(), 20).toISOString(),
              formatExp: "yyyy-MM-dd'T'HH:mm:00",
              tz: 'UTC',
            }),
        dateTo: accountExtraitTransactionListFilters.dateTo
          ? accountExtraitTransactionListFilters.dateTo
          : stringToDate({
              date: subMinutes(new Date(), 1).toISOString(),
              formatExp: "yyyy-MM-dd'T'HH:mm:00",
              tz: 'UTC',
            }),
        type: accountExtraitTransactionsToDownload?.type,
        transactionType: mappingTransactionType(t, accountExtraitTransactionListFilters.transactionType),
        meta: accountExtraitTransactionsToDownload?.meta,
      },
      Boolean(accountExtraitTransactionsToDownload),
    );

  useEffect(() => {
    if (accountExtraitTransactionsDownloadData && accountExtraitTransactionsToDownload) {
      downloadFile(
        `${t('accountExtraitTransactionsPage.CardHeading.Transactions')} ${accountNumber}`,
        accountExtraitTransactionsDownloadData.report,
        accountExtraitTransactionsToDownload.type,
      );
      setAccountExtraitTransactionsToDownload(null);
    }
  }, [accountExtraitTransactionsDownloadData, accountExtraitTransactionsToDownload, accountNumber, t]);

  const onDownloadAccountExtraitTransactions = (type: DownloadTypes) => {
    setAccountExtraitTransactionsToDownload({ type, meta: { timestamp: new Date().toISOString() } });
  };

  return { isLoadingAccountExtraitTransactionsDownloadData, onDownloadAccountExtraitTransactions };
};

export { useDownloadAccountExtraitTransactions };
